import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformServer} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {of} from 'rxjs';
import {catchError, take, tap} from 'rxjs/operators';

@Injectable()
export class PriceService {
  private readonly currencyRates = {eur: 0.70};
  private prices = {};

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: string
  ){}

  public fetchPrices(version?: number) {
    const requiredVersion = version ? version : 'latest';
    if (this.prices[requiredVersion]) {
      return of(this.prices[requiredVersion]);
    } else {
      const prices = this.http.get('/prices/' + requiredVersion).pipe(
        tap(prices => {
          this.prices[requiredVersion] = prices;
        }),
        catchError(error => {
          if (isPlatformServer(this.platformId)) {
            console.error('Failed to fetch prices:', error.status || error.message);
          }
          return of(null);
        })
      );
      return prices
    }
  }

  async getServicePrice(service, skipServerCheck = false): Promise<{ price: number, discountedPrice: number, oldPrice: number, cashback: number, sd: string, campaignDiscount: number, tierDiscountFeature: any, subtotal:number, baseCampaignDiscount: number }> {
    const resp = await this.http.post<any>('/prices/calculate', service)
      .pipe(take(1), catchError(e => of({error: 'Failed to calculate price!'})))
      .toPromise();
    if (resp.error) {
      throw new Error(resp.error);
    }

    return resp;
  }

  calculateRemainingServicePrice(order) {
    const newService = {
      type: order.type,
      service: order.service,
      currency: order.currency,
      isExpress: order.isExpress,
      isSoloOnly: order.isSoloOnly,
      isWithStreaming: order.isWithStreaming,
      isWithVoice: order.isWithVoice,
      isWithCharacters: order.isWithCharacters,
      isWithPaidCharacters: order.isWithPaidCharacters,
      selectedAdditions: order.selectedAdditions,
      game: order.game,
      server: order.server,
      queue: order.queue,
      current: null,
      desired: null,
      lp: null,
      wins: null,
      losses: null,
      promos: null,
      activeTierIndex: 0
    };

    if (newService.service === 'placements') {
      newService.current = order.startingRank;
      newService.desired = order.desiredGames - order.currentGames;
    }

    if (newService.service === 'normalgames') {
      newService.desired = order.desiredWins - order.currentWins;
    }

    if (newService.service === 'netwins') {
      newService.current = order.startingRank;
      newService.desired = order.desiredWins - order.currentWins;
    }

    if (newService.service === 'games') {
      newService.current = order.startingRank;
      newService.desired = order.desiredGames - order.currentGames;
    }

    if (newService.service === 'masteries') {
      newService.current = order.currentRank;
      newService.desired = order.desiredRank;
    }

    if (newService.service === 'leveling' || newService.service === 'proficiencies') {
      newService.current = order.currentRank;
      newService.desired = order.desiredRank;
    }

    if (newService.service === 'divisions' || newService.service === 'doubleup' ) {
      newService.current = order.currentRank;
      newService.desired = order.desiredRank;
      newService.lp = order.currentLp;
      newService.promos = order.currentPromos;
      newService.wins = (order.currentPromos ? (order.currentPromos.match(/w/g) || []).length : 0);
      newService.losses = (order.currentPromos ? (order.currentPromos.match(/l/g) || []).length : 0);
    }

    if (newService.service === 'badges') {
      newService.current = order.currentRank;
      newService.desired = order.desiredRank;
    }

    return this.getServicePrice(newService, true);
  }
}
